/**
 * One follow-up, opened from the sales report.
 *
 * The report is a place to read, not to work: the row says a person is
 * three days into being chased, and the next question is always "chased
 * about what, and what did they say". That answer already exists on the
 * Follow-ups page, so this shows the same three panels rather than a
 * second, thinner version of them — who is being called, the trail of
 * calling them, and the booking the whole thing came out of.
 *
 * Read-only on purpose. Closing a follow-up moves it between lists, and
 * doing that from behind a report leaves somebody looking at a row that
 * has since gone somewhere else. The footer link goes to the page that
 * owns the work.
 */
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../store/AppContext.jsx';
import Icon from '../../lib/icons.jsx';
import Sheet from '../Sheet.jsx';
import { EmptyState, Pill } from '../Primitives.jsx';
import { ClientCard, FollowJourney } from '../FollowJourney.jsx';
import DealJourney from '../DealJourney.jsx';
import { BookingDetail } from '../../pages/DemoBookings.jsx';
import { followState, stamp } from '../../domain/demos.js';
import AskAI from "../AskAI.jsx";

/* What the state is called, and how it is coloured, matching the row. */
const STATE = {
  handed: ['Not started', 'gr-amber'],
  following: ['Following up', 'gr-violet'],
  closed: ['Closed', 'gr-teal']
};

export default function FollowUpView({ id, onClose }) {
  const { demos } = useApp();
  const navigate = useNavigate();

  const b = (demos?.bookings || []).find(x => x.id === id);

  if (!b) {
    return (
      <Sheet icon="flag" tone="g-blue" title="Follow-up" onClose={onClose} closeLabel="Close" wide>
        <div style={{ gridColumn: '1/-1' }}>
          <EmptyState icon="flag" tone="g-slate" title="Not here any more">
            This follow-up is no longer in the loaded bookings — it may have been
            reassigned or the list may have been refreshed since the report drew.
          </EmptyState>
        </div>
      </Sheet>
    );
  }

  const s = b.session || {};
  const [label, tone] = STATE[followState(b)] || STATE.handed;
  const rounds = (s.feedback || []).length;

  return (
    <Sheet
      icon="flag"
      tone="g-blue"
      title={b.invitee?.name || b.invitee?.email || 'Follow-up'}
      badge={<Pill tone={tone}>{label}</Pill>}
      onClose={onClose}
      closeLabel="Close"
      wide
      bodyClass="dbk-detail"
      hero={(
        <div className="fuvhero">
          <div>
            <b>{rounds}</b>
            <small>round{rounds === 1 ? '' : 's'} logged</small>
          </div>
          <div>
            <b>{s.handedBy || '—'}</b>
            <small>handed it across{s.handedAt ? ` · ${stamp(s.handedAt)}` : ''}</small>
          </div>
          {s.nextDue ? (
            <div>
              <b>{s.nextDue}</b>
              <small>due next</small>
            </div>
          ) : null}
        </div>
      )}
      footerLeft={(
        <>
          {/* Read against the customer record, not the booking: the question
              is whether the trial converts, and that is answered by the plan,
              the days left and who has spoken to them. */}
          {b.client ? (
            <AskAI
              module="trial"
              id={b.client.id}
              subject={b.client.name || b.invitee.name}
              label="Coach this trial"
            />
          ) : null}
          <button
            type="button"
            className="btn"
            onClick={() => { onClose(); navigate('/follow-ups'); }}
          >
            <Icon name="flag" />Work on it in Follow-ups
          </button>
        </>
      )}
    >
      <ClientCard b={b} />

      {/* The whole trail first, then the detail behind it. The rounds
          below are the chase after the hand-over; this is the same rounds
          with the booking, the demo and — where the person was a pipeline
          lead as well — the calls and the stage moves in their places. */}
      <section className="vpanel wide">
        <h4><span className="ic g-purple"><Icon name="zap" /></span>Journey</h4>
        <div className="vbody"><DealJourney booking={b} limit={14} /></div>
      </section>

      <FollowJourney b={b} readOnly />
      <BookingDetail b={b} data={demos} />
    </Sheet>
  );
}
